import { customerNumberSchema, type TenantDetail } from '@avrahami/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  fill,
  formatDateTime,
  isApiError,
  TextField,
} from '@avrahami/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { keys, platformApi } from '../api/platform';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';

/**
 * Why the API refused a customer number (adr/0038), in words, or undefined when it refused it
 * for another reason. The API names the reason in the detail of `customerNumber`.
 */
export function customerRefusal(error: unknown): string | undefined {
  if (isApiError(error, 'SERVICE_UNAVAILABLE')) return he.tenant.customerErrors.unavailable;
  if (!isApiError(error, 'BAD_REQUEST')) return undefined;
  const detail = error.details.find((item) => item.path === 'customerNumber')?.message;
  if (detail === 'not a customer of the original store') return he.tenant.customerErrors.notFound;
  if (detail === 'customer not active in the original store') {
    return he.tenant.customerErrors.inactive;
  }
  if (detail === 'customer has no price list') return he.tenant.customerErrors.noPriceList;
  return undefined;
}

/** The API's message when another store is already linked to the customer. */
export const CUSTOMER_TAKEN = 'Another store already has this customer number';

/**
 * The customer of Avrahami's original store the store is (adr/0038): every price, cost and fact
 * of its catalog products comes from what the original store decided for that customer. The
 * staff link the store to another customer, or read the customer again.
 */
export function StoreCustomerCard({
  detail,
  canChange,
}: {
  detail: TenantDetail;
  canChange: boolean;
}) {
  const tenantId = detail.tenant.id;
  const { customerNumber, customer } = detail;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const save = useMutation({
    mutationFn: (number: string) => platformApi.updateCustomerNumber(tenantId, number),
    onSuccess: (next) => {
      queryClient.setQueryData(keys.tenant(tenantId), next);
      setEditing(false);
    },
  });
  const refusal = customerRefusal(save.error);
  const failure = save.isError ? (
    <Alert>
      {isApiError(save.error, 'CONFLICT') && save.error.message === CUSTOMER_TAKEN
        ? he.tenant.customerNumberTaken
        : (refusal ?? errorMessage(save.error))}
    </Alert>
  ) : null;

  return (
    <Card title={he.tenant.customerTitle}>
      <div className="flex flex-col gap-4 text-sm" data-testid="store-customer">
        <p className="text-slate-600">{he.tenant.customerIntro}</p>
        {customerNumber === null ? <Alert tone="warning">{he.tenant.customerMissing}</Alert> : null}
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
          <dt className="text-slate-500">{he.tenant.customerNumber}</dt>
          <dd
            data-testid="tenant-customer-number"
            className={customerNumber === null ? 'text-slate-500' : 'ltr font-mono'}
          >
            {customerNumber ?? he.tenant.customerNumberNone}
          </dd>
          {customer === null ? null : (
            <>
              <dt className="text-slate-500">{he.tenant.customerName}</dt>
              <dd data-testid="store-customer-name">{customer.name ?? '—'}</dd>
              <dt className="text-slate-500">{he.tenant.customerState}</dt>
              <dd>
                {customer.active ? (
                  <Badge tone="green">{he.tenant.customerActive}</Badge>
                ) : (
                  <Badge tone="red">{he.tenant.customerInactive}</Badge>
                )}
              </dd>
              <dt className="text-slate-500">{he.tenant.customerPriceList}</dt>
              <dd data-testid="store-customer-price-list">{customer.priceList ?? '—'}</dd>
              <dt className="text-slate-500">{he.tenant.customerPrices}</dt>
              <dd data-testid="store-customer-prices">
                {fill(he.tenant.customerPricesValue, {
                  count: customer.pricedItems,
                  special: customer.specialPrices,
                })}
              </dd>
              <dt className="text-slate-500">{he.tenant.customerCheckedAt}</dt>
              <dd>{formatDateTime(customer.checkedAt)}</dd>
            </>
          )}
        </dl>
        {customer !== null && !customer.active ? (
          <Alert tone="warning">{he.tenant.customerInactiveNote}</Alert>
        ) : null}
        {customerNumber === null ? null : (
          <span className="text-xs text-slate-500">
            {he.tenant.skuExample}: <span className="ltr font-mono">8163/{customerNumber}</span>
          </span>
        )}
        {save.isSuccess ? (
          <span role="status" className="text-xs text-green-800">
            {he.tenant.customerNumberSaved}
          </span>
        ) : null}
        {!canChange ? null : editing ? (
          <form
            className="flex max-w-sm flex-col gap-2"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              // The rule the API checks with; the API then asks the original store.
              const parsed = customerNumberSchema.safeParse(value);
              setInvalid(!parsed.success);
              if (parsed.success) save.mutate(parsed.data);
            }}
          >
            <TextField
              label={he.tenant.customerNumberLabel}
              name="customerNumber"
              autoComplete="off"
              className="ltr"
              value={value}
              hint={he.tenant.customerNumberHint}
              error={
                invalid || isApiError(save.error, 'VALIDATION_FAILED')
                  ? he.common.invalidField
                  : undefined
              }
              onChange={(event) => {
                setInvalid(false);
                setValue(event.target.value);
              }}
            />
            {isApiError(save.error, 'VALIDATION_FAILED') ? null : failure}
            <div className="flex gap-2">
              <Button type="submit" busy={save.isPending} data-testid="tenant-customer-number-save">
                {he.tenant.customerNumberSave}
              </Button>
              <Button
                tone="secondary"
                disabled={save.isPending}
                onClick={() => {
                  setEditing(false);
                }}
              >
                {he.tenant.customerNumberCancel}
              </Button>
            </div>
          </form>
        ) : (
          <>
            {failure}
            <div className="flex flex-wrap gap-2">
              {customerNumber === null ? null : (
                <Button
                  tone="secondary"
                  busy={save.isPending}
                  data-testid="store-customer-refresh"
                  onClick={() => {
                    save.mutate(customerNumber);
                  }}
                >
                  {he.tenant.customerRefresh}
                </Button>
              )}
              <Button
                tone="secondary"
                disabled={save.isPending}
                data-testid="tenant-customer-number-edit"
                onClick={() => {
                  setValue(customerNumber ?? '');
                  save.reset();
                  setEditing(true);
                }}
              >
                {customerNumber === null
                  ? he.tenant.customerNumberSet
                  : he.tenant.customerNumberEdit}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
